import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useOwnerCheck } from "@/hooks/useOwnerCheck";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ShieldAlert, RefreshCw } from "lucide-react";

const TIMEOUT = 9000;

export const OwnerRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const { isOwner, loading: ownerLoading } = useOwnerCheck();
  const navigate = useNavigate();
  const actedRef = useRef(false);
  const [timedOut, setTimedOut] = useState(false);

  const allLoading = authLoading || ownerLoading;
  const denied = !authLoading && !ownerLoading && !!user && !isOwner;

  useEffect(() => {
    if (!allLoading) { setTimedOut(false); return; }
    const t = setTimeout(() => setTimedOut(true), TIMEOUT);
    return () => clearTimeout(t);
  }, [allLoading]);

  useEffect(() => { actedRef.current = false; }, [user?.id]);

  useEffect(() => {
    if (!authLoading && !user && !actedRef.current) {
      actedRef.current = true;
      navigate("/auth/login", { replace: true });
      return;
    }
    if (denied && !actedRef.current) {
      actedRef.current = true;
      toast.error("Acceso restringido — solo Owner", { id: "owner-only" });
      navigate("/dashboard", { replace: true });
    }
  }, [authLoading, denied, user, navigate]);

  if (denied) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
            <ShieldAlert className="w-8 h-8 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold">Acceso restringido</h2>
          <p className="text-sm text-muted-foreground">
            Este panel está reservado al rol Owner.
          </p>
          <Button onClick={() => navigate("/dashboard", { replace: true })}>Volver al dashboard</Button>
        </div>
      </div>
    );
  }

  if (allLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Verificando permisos…</p>
          {timedOut && (
            <Button variant="outline" size="sm" className="gap-2 mt-3" onClick={() => window.location.reload()}>
              <RefreshCw className="w-4 h-4" /> Reintentar
            </Button>
          )}
        </div>
      </div>
    );
  }

  return user && isOwner ? <>{children}</> : null;
};
